/**
 * STAFF DASHBOARD
 * 
 * When a user accesses their workplace, this dashboard appears.
 * Shows only the features they have permission to access.
 * All data comes from the business's backend, not the personal app.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { workplacesApi } from '../services/api';

interface WorkplaceContext {
  businessId: string;
  businessName: string;
  businessLogoUrl?: string;
  role: string;
  permissions: string[];
  features: string[];
}

interface TodayStats {
  todayHours: number;
  weekHours: number;
  todayEarnings: number;
  weekEarnings: number;
  clockedIn: boolean;
  clockInTime?: string;
}

interface Booking {
  id: string;
  time: string;
  service: string;
  customerName: string;
  duration: string;
  status: string;
}

interface SpendingWallet {
  dailyLimit: number;
  dailySpent: number;
  weeklyLimit: number;
  weeklySpent: number;
  allowedCategories: string[];
  mangoPayEnabled: boolean;
  virtualCardEnabled: boolean;
}

const StaffDashboard: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { user, staffAccounts } = useAuth();

  const [workplace, setWorkplace] = useState<WorkplaceContext | null>(null);
  const [showBusinessSwitcher, setShowBusinessSwitcher] = useState(false);
  const [stats, setStats] = useState<TodayStats | null>(null);
  const [todayBookings, setTodayBookings] = useState<Booking[]>([]);
  const [spendingWallet, setSpendingWallet] = useState<SpendingWallet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (businessId) {
      loadWorkplaceData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load on businessId change only
  }, [businessId]);

  const loadWorkplaceData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get workplace context (from localStorage or API)
      const storedWorkplace = localStorage.getItem('activeWorkplace');
      if (storedWorkplace) {
        const parsed = JSON.parse(storedWorkplace);
        if (parsed.businessId === businessId) {
          setWorkplace(parsed);
        }
      }

      // Load staff data for this workplace
      const [statsRes, bookingsRes, walletRes] = await Promise.all([
        workplacesApi.getMyStats(businessId!),
        workplacesApi.getTodayBookings(businessId!),
        workplacesApi.getMySpendingWallet(businessId!),
      ]);

      if (statsRes.success) setStats(statsRes.data);
      if (bookingsRes.success) setTodayBookings(bookingsRes.data || []);
      if (walletRes.success) setSpendingWallet(walletRes.data);
    } catch (err) {
      console.error('Failed to load workplace data:', err);
      setError('Failed to load workplace data');
    } finally {
      setLoading(false);
    }
  };

  const handleClockInOut = async () => {
    try {
      if (stats?.clockedIn) {
        await workplacesApi.clockOut(businessId!);
      } else {
        await workplacesApi.clockIn(businessId!);
      }
      loadWorkplaceData(); // Refresh
    } catch (err) {
      console.error('Clock in/out failed:', err);
    }
  };

  const handleBackToWorkplaces = () => {
    localStorage.removeItem('activeWorkplace');
    navigate('/workplaces');
  };

  const formatHours = (hours?: number) => {
    if (!hours) return '0h';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(amount);
  };

  const hasFeature = (feature: string) => {
    return workplace?.features.includes(feature) || false;
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FFF8E7' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>💼</div>
          <p>Loading workplace...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FFF8E7' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⚠️</div>
          <p>{error}</p>
          <button onClick={handleBackToWorkplaces}>← Back to Workplaces</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#FFF8E7', paddingBottom: '100px' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
        padding: '1rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
      }}>
        <button
          onClick={handleBackToWorkplaces}
          style={{
            background: 'rgba(0,0,0,0.1)',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.25rem',
            cursor: 'pointer',
          }}
        >
          ←
        </button>
        <div style={{ flex: 1, position: 'relative' }}>
          <button
            onClick={() => staffAccounts?.length > 1 && setShowBusinessSwitcher(!showBusinessSwitcher)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: staffAccounts?.length > 1 ? 'pointer' : 'default',
              textAlign: 'left',
            }}
          >
            <h1 style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>
              {workplace?.businessName || 'Staff Dashboard'}
              {staffAccounts?.length > 1 && <span style={{ marginLeft: 4, opacity: 0.8 }}>▾</span>}
            </h1>
            <p style={{ margin: 0, opacity: 0.8, fontSize: '0.9rem' }}>
              {workplace?.role}
            </p>
          </button>
          {showBusinessSwitcher && staffAccounts && staffAccounts.length > 1 && (
            <>
              <div
                style={{ position: 'fixed', inset: 0, zIndex: 98 }}
                onClick={() => setShowBusinessSwitcher(false)}
              />
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                padding: '8px',
                minWidth: '200px',
                marginTop: '8px',
                zIndex: 99,
              }}>
                {staffAccounts.map((sa) => (
                  <button
                    key={sa.id}
                    onClick={() => {
                      setShowBusinessSwitcher(false);
                      localStorage.setItem('activeWorkplace', JSON.stringify({
                        businessId: sa.businessId,
                        businessName: sa.business.name,
                        role: sa.role,
                        permissions: {},
                        features: ['pos', 'schedule', 'customers', 'orders', 'content', 'spending'],
                      }));
                      navigate(`/workplace/${sa.businessId}`);
                    }}
                    style={{
                      width: '100%',
                      background: sa.businessId === businessId ? '#fff3e0' : 'transparent',
                      border: 'none',
                      color: '#333',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      fontSize: '14px',
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{sa.business.name}</span>
                    <span style={{ fontSize: '12px', color: '#666' }}>{sa.role}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <div style={{
          width: '45px',
          height: '45px',
          borderRadius: '50%',
          background: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
        }}>
          {user?.firstName?.charAt(0) || '?'}
        </div>
      </div>

      <div style={{ padding: '1rem' }}>
        {/* Clock In/Out Card */}
        <div style={{
          background: stats?.clockedIn ? '#32CD32' : 'white',
          color: stats?.clockedIn ? 'white' : 'black',
          borderRadius: '1rem',
          padding: '1.5rem',
          marginBottom: '1rem',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>
            {stats?.clockedIn ? '🟢' : '⏰'}
          </div>
          <h2 style={{ margin: '0 0 0.5rem' }}>
            {stats?.clockedIn ? 'You\'re Clocked In' : 'Ready to Start?'}
          </h2>
          {stats?.clockedIn && stats.clockInTime && (
            <p style={{ margin: '0 0 1rem', opacity: 0.8 }}>
              Since {new Date(stats.clockInTime).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
          <button
            onClick={handleClockInOut}
            style={{
              background: stats?.clockedIn ? 'rgba(255,255,255,0.2)' : '#FFD700',
              color: stats?.clockedIn ? 'white' : 'black',
              border: 'none',
              borderRadius: '0.5rem',
              padding: '0.75rem 2rem',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
          >
            {stats?.clockedIn ? '🛑 Clock Out' : '▶️ Clock In'}
          </button>
        </div>

        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '0.75rem',
          marginBottom: '1rem',
        }}>
          <div style={{ background: 'white', borderRadius: '0.75rem', padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '0.8rem', color: '#666' }}>Today</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{formatHours(stats?.todayHours)}</div>
          </div>
          <div style={{ background: 'white', borderRadius: '0.75rem', padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '0.8rem', color: '#666' }}>This Week</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{formatHours(stats?.weekHours)}</div>
          </div>
          <div style={{ background: 'white', borderRadius: '0.75rem', padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '0.8rem', color: '#666' }}>Today Earnings</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#32CD32' }}>
              {formatCurrency(stats?.todayEarnings || 0)}
            </div>
          </div>
          <div style={{ background: 'white', borderRadius: '0.75rem', padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '0.8rem', color: '#666' }}>Week Earnings</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#32CD32' }}>
              {formatCurrency(stats?.weekEarnings || 0)}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <h3 style={{ margin: '1.5rem 0 0.75rem', fontSize: '1rem' }}>Quick Actions</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '0.75rem',
          marginBottom: '1.5rem',
        }}>
          {hasFeature('pos') && (
            <button
              onClick={() => navigate(`/workplace/${businessId}/pos`)}
              style={{
                background: 'white',
                border: 'none',
                borderRadius: '0.75rem',
                padding: '1rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: '1.75rem', marginBottom: '0.25rem' }}>💳</span>
              <span style={{ fontSize: '0.8rem', fontWeight: '500' }}>POS</span>
            </button>
          )}
          {hasFeature('schedule') && (
            <button
              onClick={() => navigate(`/workplace/${businessId}/schedule`)}
              style={{
                background: 'white',
                border: 'none',
                borderRadius: '0.75rem',
                padding: '1rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: '1.75rem', marginBottom: '0.25rem' }}>📅</span>
              <span style={{ fontSize: '0.8rem', fontWeight: '500' }}>Schedule</span>
            </button>
          )}
          {hasFeature('customers') && (
            <button
              onClick={() => navigate(`/workplace/${businessId}/customers`)}
              style={{
                background: 'white',
                border: 'none',
                borderRadius: '0.75rem',
                padding: '1rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: '1.75rem', marginBottom: '0.25rem' }}>👥</span>
              <span style={{ fontSize: '0.8rem', fontWeight: '500' }}>Customers</span>
            </button>
          )}
          {hasFeature('orders') && (
            <button
              onClick={() => navigate(`/workplace/${businessId}/orders`)}
              style={{
                background: 'white',
                border: 'none',
                borderRadius: '0.75rem',
                padding: '1rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: '1.75rem', marginBottom: '0.25rem' }}>📦</span>
              <span style={{ fontSize: '0.8rem', fontWeight: '500' }}>Orders</span>
            </button>
          )}
          {hasFeature('content') && (
            <button
              onClick={() => navigate(`/workplace/${businessId}/content`)}
              style={{
                background: 'white',
                border: 'none',
                borderRadius: '0.75rem',
                padding: '1rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: '1.75rem', marginBottom: '0.25rem' }}>📱</span>
              <span style={{ fontSize: '0.8rem', fontWeight: '500' }}>Content</span>
            </button>
          )}
          {hasFeature('spending') && (
            <button
              onClick={() => navigate(`/workplace/${businessId}/spending`)}
              style={{
                background: 'white',
                border: 'none',
                borderRadius: '0.75rem',
                padding: '1rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: '1.75rem', marginBottom: '0.25rem' }}>💰</span>
              <span style={{ fontSize: '0.8rem', fontWeight: '500' }}>Spending</span>
            </button>
          )}
        </div>

        {/* Spending Wallet (if enabled) */}
        {hasFeature('spending') && spendingWallet && (
          <div style={{
            background: 'linear-gradient(135deg, #4169E1, #8A2BE2)',
            color: 'white',
            borderRadius: '1rem',
            padding: '1.25rem',
            marginBottom: '1rem',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>💳 Business Spending</h3>
              {spendingWallet.mangoPayEnabled && (
                <span style={{ background: 'rgba(255,255,255,0.2)', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.7rem' }}>
                  Mango Pay ✓
                </span>
              )}
            </div>
            
            {/* Daily Limit */}
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                <span>Daily</span>
                <span>{formatCurrency(spendingWallet.dailySpent)} / {formatCurrency(spendingWallet.dailyLimit)}</span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '0.25rem', height: '8px', overflow: 'hidden' }}>
                <div style={{
                  width: `${Math.min((spendingWallet.dailySpent / spendingWallet.dailyLimit) * 100, 100)}%`,
                  height: '100%',
                  background: 'white',
                  borderRadius: '0.25rem',
                }} />
              </div>
            </div>
            
            {/* Weekly Limit */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                <span>Weekly</span>
                <span>{formatCurrency(spendingWallet.weeklySpent)} / {formatCurrency(spendingWallet.weeklyLimit)}</span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '0.25rem', height: '8px', overflow: 'hidden' }}>
                <div style={{
                  width: `${Math.min((spendingWallet.weeklySpent / spendingWallet.weeklyLimit) * 100, 100)}%`,
                  height: '100%',
                  background: 'white',
                  borderRadius: '0.25rem',
                }} />
              </div>
            </div>

            <button
              onClick={() => navigate(`/workplace/${businessId}/spending`)}
              style={{
                width: '100%',
                marginTop: '1rem',
                padding: '0.75rem',
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              💳 Make a Purchase
            </button>
          </div>
        )}

        {/* Today's Bookings */}
        {hasFeature('schedule') && (
          <>
            <h3 style={{ margin: '1.5rem 0 0.75rem', fontSize: '1rem' }}>📅 Today's Bookings</h3>
            {todayBookings.length === 0 ? (
              <div style={{
                background: 'white',
                borderRadius: '0.75rem',
                padding: '2rem',
                textAlign: 'center',
                color: '#666',
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📅</div>
                No bookings scheduled for today
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {todayBookings.map(booking => (
                  <div
                    key={booking.id}
                    style={{
                      background: 'white',
                      borderRadius: '0.75rem',
                      padding: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                    }}
                  >
                    <div style={{
                      background: '#FFD700',
                      borderRadius: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                    }}>
                      {booking.time}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500' }}>{booking.service}</div>
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>
                        {booking.customerName} • {booking.duration}
                      </div>
                    </div>
                    <span style={{
                      background: booking.status === 'confirmed' ? '#E8F5E9' : '#FFF3E0',
                      color: booking.status === 'confirmed' ? '#2E7D32' : '#EF6C00',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.25rem',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                    }}>
                      {booking.status.toUpperCase()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom Nav */}
      <nav style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'white',
        borderTop: '1px solid #eee',
        display: 'flex',
        justifyContent: 'space-around',
        padding: '0.75rem 0',
      }}>
        <button
          onClick={() => navigate(`/workplace/${businessId}`)}
          style={{
            background: 'none',
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            color: '#FFD700',
            cursor: 'pointer',
          }}
        >
          <span style={{ fontSize: '1.5rem' }}>🏠</span>
          <span style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>Home</span>
        </button>
        {hasFeature('schedule') && (
          <button
            onClick={() => navigate(`/workplace/${businessId}/schedule`)}
            style={{
              background: 'none',
              border: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              color: '#666',
              cursor: 'pointer',
            }}
          >
            <span style={{ fontSize: '1.5rem' }}>📅</span>
            <span style={{ fontSize: '0.7rem' }}>Schedule</span>
          </button>
        )}
        {hasFeature('pos') && (
          <button
            onClick={() => navigate(`/workplace/${businessId}/pos`)}
            style={{
              background: 'none',
              border: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              color: '#666',
              cursor: 'pointer',
            }}
          >
            <span style={{ fontSize: '1.5rem' }}>💳</span>
            <span style={{ fontSize: '0.7rem' }}>POS</span>
          </button>
        )}
        <button
          onClick={handleBackToWorkplaces}
          style={{
            background: 'none',
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            color: '#666',
            cursor: 'pointer',
          }}
        >
          <span style={{ fontSize: '1.5rem' }}>💼</span>
          <span style={{ fontSize: '0.7rem' }}>Switch</span>
        </button>
      </nav>
    </div>
  );
};

export default StaffDashboard;
